import {
  formatBRL,
  fromCents,
  fromWire,
  type Money,
  ORIGENS_DE_CUSTO,
  type OrigemDoCusto,
  ROTULO_DO_TIPO_DE_CUSTO,
  sumAll,
  TAMANHO_MAXIMO_DE_PAGINA,
  type TipoDeCustoDeVeiculo,
  TIPOS_DE_CUSTO_DE_VEICULO,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Banknote,
  Calculator,
  CalendarCheck,
  EllipsisVertical,
  ExternalLink,
  type LucideIcon,
  Pencil,
  Plus,
  Receipt,
  Search,
  SlidersHorizontal,
  Undo2,
} from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  competenciaAtual,
  type Custo,
  type FiltrosDeVeiculos,
  identificacaoDoVeiculo,
  type LinhaDoResumoDeCusto,
  rotuloDaCompetencia,
  useCustos,
  useEstornarCusto,
  useFecharCompetencia,
  usePoliticaDeCusto,
  useResumoDeCustos,
  useVeiculos,
} from '@/features/frota/api'
import { BadgeDeOrigemDeCusto } from '@/features/frota/badges'
import { DialogoDeCusto } from '@/features/frota/dialogo-de-custo'
import {
  DialogoDePoliticaDeCusto,
  formatarPontosBaseComoPercentual,
} from '@/features/frota/dialogo-de-politica-de-custo'
import { errorMessage } from '@/lib/api'
import { formatarData, SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'

/**
 * Custos da frota.
 *
 * ---------------------------------------------------------------------------
 * A TELA EXISTE PARA TORNAR IMPOSSÍVEL SOMAR CAIXA COM COMPETÊNCIA
 * ---------------------------------------------------------------------------
 * CAIXA (`LANCAMENTO_MANUAL`) é o que saiu do banco no mês: o IPVA inteiro em
 * janeiro, a lavagem de ontem. COMPETÊNCIA (`CALCULO_AUTOMATICO`) é o que
 * aquele mês consumiu: depreciação, custo de capital, a fatia do overhead e os
 * duodécimos do MESMO IPVA ao longo do ano.
 *
 * O mesmo pagamento aparece nas duas. Somá-las conta o dinheiro duas vezes e
 * dobra o custo da frota no relatório — um número errado por um fator de dois é
 * pior do que número nenhum, porque parece plausível.
 *
 * Por isso, em lugar nenhum desta tela existe uma coluna "total": os dois
 * números aparecem SEMPRE lado a lado, rotulados, com origem em toda linha da
 * listagem. A quebra por tipo é somada apenas DENTRO de uma origem, nunca entre
 * elas, e a soma usa `sumAll` (bigint exato) — nenhum `number` toca em dinheiro
 * neste arquivo.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE DUAS VEZES
 * ---------------------------------------------------------------------------
 * O CTA "Lançar custo" e o cartão de COMPETÊNCIA. O segundo é o número que o
 * Gap 7 existe para revelar: é ele que mostra o custo que o extrato bancário
 * esconde, e é onde o olho do gestor precisa parar. Todo o resto é cinza,
 * âmbar e vermelho semânticos.
 *
 * As barras da tabela são a exceção que confirma a regra: "série primária em
 * `--brand-500`" vale para um gráfico com poucas séries, não para duas barras
 * repetidas em vinte linhas — vinte tracinhos amarelos deixariam a tela
 * amarela e o cartão de destaque perderia a única coisa que o destaca. Elas
 * usam a escala de cinza frio (`chart-2` e `chart-4`), e quem separa as duas
 * colunas é o cabeçalho, não a cor.
 */

const TAMANHO_DA_PAGINA = 20

/** Um sanduíche de zeros largo o bastante para qualquer valor que caiba num `BIGINT`. */
const LARGURA_DA_CHAVE_DE_VALOR = 20

/**
 * A frota inteira, baixados inclusive — custo de veículo baixado continua
 * existindo e precisa ser filtrável. O objeto é constante de módulo (chave de
 * cache estável) e tem a MESMA forma da constante do diálogo de custo, então as
 * duas telas dividem uma requisição só.
 */
const TODOS_OS_VEICULOS: FiltrosDeVeiculos = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  busca: '',
  categoryId: '',
  locationId: '',
  status: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: true,
}

/**
 * O rótulo do filtro de origem diz o que a linha SIGNIFICA, não como ela
 * nasceu: `LANCAMENTO_MANUAL` descreve o mecanismo, e quem lê o relatório
 * precisa saber que aquilo é caixa. Os selos de `features/frota/badges` usam as
 * mesmas duas palavras.
 */
const ROTULO_DA_ORIGEM: Readonly<Record<OrigemDoCusto, string>> = {
  LANCAMENTO_MANUAL: 'Caixa — saiu do banco no mês',
  CALCULO_AUTOMATICO: 'Competência — o mês consumiu',
}

/**
 * Centavos de uma string de wire, sem exceção que derrube a linha inteira.
 *
 * Valor ilegível vira zero na APRESENTAÇÃO (barra e agregado); o valor exibido
 * continua saindo de `formatarMoedaDoWire`, que mostra um traço nesse caso — ou
 * seja, o erro fica visível em vez de virar um número inventado.
 */
function centavos(valor: string): Money {
  try {
    return fromWire(valor)
  } catch {
    return fromCents(0)
  }
}

/**
 * Chave de ORDENAÇÃO de uma coluna monetária. Não é o valor: é a string de
 * centavos preenchida com zeros à esquerda, que ordena lexicograficamente na
 * mesma ordem do número — sem passar por `Number`, que perde precisão acima de
 * 2^53 e é proibido em dinheiro (invariante nº 1). Custo é sempre positivo (o
 * schema recusa zero), então não há sinal a tratar.
 */
function chaveDeOrdenacaoDeValor(valor: string): string {
  return valor.padStart(LARGURA_DA_CHAVE_DE_VALOR, '0')
}

interface TotalDeTipo {
  readonly tipo: TipoDeCustoDeVeiculo
  readonly valor: Money
}

/**
 * A quebra por tipo da frota inteira, DENTRO de uma origem só.
 *
 * O servidor devolve a quebra por veículo e os dois totais da frota; agregar
 * por tipo é a única soma feita no painel, e ela nunca cruza as origens — o
 * parâmetro é justamente qual das duas está sendo somada. `sumAll` é `bigint`
 * exato, do `@locaveri/shared`.
 *
 * A ordem é a do enum de domínio, e não a de chegada: a lista tem que ficar
 * igual entre um mês e outro, senão comparar duas competências vira caça ao
 * item que mudou de lugar.
 */
function somarPorTipo(
  itens: readonly LinhaDoResumoDeCusto[],
  origem: 'caixa' | 'competencia',
): readonly TotalDeTipo[] {
  const acumulado = new Map<TipoDeCustoDeVeiculo, Money[]>()

  for (const item of itens) {
    for (const linha of item[origem].porTipo) {
      const lista = acumulado.get(linha.tipo) ?? []
      lista.push(centavos(linha.valor))
      acumulado.set(linha.tipo, lista)
    }
  }

  return TIPOS_DE_CUSTO_DE_VEICULO.filter((tipo) => acumulado.has(tipo)).map((tipo) => ({
    tipo,
    valor: sumAll(acumulado.get(tipo) ?? []),
  }))
}

/**
 * A largura da barra, em porcentagem inteira do maior valor da tabela.
 *
 * A divisão é feita em `bigint`; `Number` só toca no RESULTADO, que é uma
 * proporção adimensional — dinheiro nenhum atravessa ponto flutuante aqui.
 */
function proporcao(valor: Money, maior: Money): number {
  if (maior <= 0n) {
    return 0
  }

  return Number((valor * 100n) / maior)
}

/**
 * Barra proporcional. As duas origens dividem a MESMA escala (o maior valor da
 * tabela, seja ele de qual coluna for), senão duas barras do mesmo tamanho
 * significariam valores diferentes — que é exatamente o engano que esta tela
 * existe para não cometer.
 *
 * A barra é `aria-hidden` de propósito: ela é redundante com o valor formatado
 * logo acima, e cor nenhuma comunica sozinha aqui.
 */
function BarraDeProporcao({
  valor,
  maior,
  cor,
}: {
  readonly valor: string
  readonly maior: Money
  readonly cor: string
}): ReactElement {
  const largura = proporcao(centavos(valor), maior)

  return (
    <div aria-hidden="true" className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-elevated">
      <div className={`h-full rounded-full ${cor}`} style={{ width: `${String(largura)}%` }} />
    </div>
  )
}

interface CartaoDeTotalProps {
  readonly titulo: string
  readonly explicacao: string
  /** Centavos como string de wire. */
  readonly total: string | undefined
  readonly porTipo: readonly TotalDeTipo[]
  readonly icone: LucideIcon
  readonly carregando: boolean
  /** A competência não pôde ser lida. */
  readonly comErro: boolean
  /** O único número com a cor da marca. Ver a nota do topo do arquivo. */
  readonly destaque?: boolean
}

/**
 * Um dos dois totais da competência.
 *
 * Os três estados da DoD são distintos AQUI, e não só na tabela abaixo:
 * carregando mostra esqueleto, erro diz que não deu para carregar, e só o
 * estado pronto pode afirmar "nenhum lançamento nesta competência". Sem essa
 * separação, uma consulta que falhou apareceria como um mês sem custo — que é
 * a mesma cara de um mês legitimamente zerado e leva à decisão errada.
 */
function CartaoDeTotal({
  titulo,
  explicacao,
  total,
  porTipo,
  icone: Icone,
  carregando,
  comErro,
  destaque = false,
}: CartaoDeTotalProps): ReactElement {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-medium text-fg-secondary">{titulo}</CardTitle>
        <Icone
          aria-hidden="true"
          className={destaque ? 'text-brand size-4' : 'size-4 text-fg-muted'}
        />
      </CardHeader>

      <CardContent>
        {carregando ? (
          <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
            <span className="sr-only">Carregando o total de {titulo}…</span>
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-3 w-full" />
          </div>
        ) : comErro ? (
          <p className="text-sm text-danger-fg">
            Não foi possível carregar este total. Use o “tentar de novo” da lista abaixo.
          </p>
        ) : (
          <>
            {/* `numeric-display` é o utilitário do tema (tabular-nums + peso 600) e
                `text-brand` respeita o contraste nos dois temas — amarelo puro como
                cor de texto reprovaria no tema claro. */}
            <p
              className={
                destaque ? 'numeric-display text-brand text-2xl' : 'numeric-display text-2xl'
              }
            >
              {formatarMoedaDoWire(total)}
            </p>
            <p className="mt-1 text-xs text-fg-muted">{explicacao}</p>

            {porTipo.length === 0 ? (
              <p className="mt-3 text-xs text-fg-muted">Nenhum lançamento nesta competência.</p>
            ) : (
              <ul className="mt-3 space-y-1 border-t border-border pt-3 text-xs">
                {porTipo.map((linha) => (
                  <li key={linha.tipo} className="flex items-center justify-between gap-3">
                    <span className="truncate text-fg-secondary">
                      {ROTULO_DO_TIPO_DE_CUSTO[linha.tipo]}
                    </span>
                    <span className="money-cell text-fg-secondary">{formatBRL(linha.valor)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface DialogoDeFechamentoProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `AAAA-MM-01`. */
  readonly competencia: string
  /** `null` enquanto a política carrega, ou quando o painel não pôde lê-la. */
  readonly politicaDefinida: boolean | null
  /** Sem `cost_policy.update` o atalho não aparece — ele levaria a um formulário só de leitura. */
  readonly podeDefinirPolitica: boolean
  readonly aoAbrirPolitica: () => void
}

/**
 * Fechamento mensal.
 *
 * Duas coisas precisam estar escritas na tela, e não só na documentação:
 *
 *  1. RODAR DUAS VEZES O MESMO MÊS É SEGURO. O fechamento é idempotente no
 *     banco (índice único parcial por competência) e a resposta diz quantas
 *     linhas JÁ EXISTIAM. Sem essa frase, ninguém clica — e um mês sem
 *     fechamento é um mês com custo subestimado.
 *  2. O QUE JÁ FOI GRAVADO NÃO É REESCRITO. Mudar a política não recalcula o
 *     passado; vale do próximo fechamento em diante.
 */
function DialogoDeFechamento({
  aberto,
  aoFechar,
  competencia,
  politicaDefinida,
  podeDefinirPolitica,
  aoAbrirPolitica,
}: DialogoDeFechamentoProps): ReactElement {
  const fechar = useFecharCompetencia()
  const resultado = fechar.data ?? null

  // O resultado do fechamento anterior não pode reaparecer na próxima abertura:
  // "3 linhas criadas" de outro mês é a pior informação possível aqui.
  useEffect(() => {
    if (aberto) {
      fechar.reset()
    }
    // `fechar` é recriado a cada render pelo TanStack Query; a intenção é
    // limpar UMA vez, na abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, competencia])

  const rotulo = rotuloDaCompetencia(competencia)
  const bloqueado = politicaDefinida === false

  function confirmar(): void {
    fechar.mutate(competencia, {
      onSuccess: (dados) => {
        toast.success(`Competência de ${rotulo} fechada`, {
          description: `${String(dados.linhasCriadas)} linhas criadas, ${String(dados.linhasJaExistentes)} já existiam.`,
        })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível rodar o fechamento', { description: errorMessage(causa) })
      },
    })
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Fechar a competência de {rotulo}</DialogTitle>
          <DialogDescription>
            Gera, para cada veículo ativo, as três linhas de competência do mês: depreciação, custo
            de capital e a fatia do overhead.
          </DialogDescription>
        </DialogHeader>

        {resultado === null ? (
          <div className="space-y-4">
            {bloqueado ? (
              <Alert variant="warning">
                <AlertTitle>A política de custo ainda não foi salva</AlertTitle>
                <AlertDescription>
                  Sem vida útil, residual, custo de capital e overhead definidos, o fechamento
                  recusa rodar — ele não inventa premissa.
                  {podeDefinirPolitica
                    ? ''
                    : ' Peça a quem pode alterar os parâmetros de custo indireto.'}
                </AlertDescription>
              </Alert>
            ) : null}

            <Alert variant="info">
              <AlertTitle>Rodar duas vezes o mesmo mês é seguro</AlertTitle>
              <AlertDescription>
                O fechamento é idempotente: nada é duplicado e o que já foi gravado não é reescrito.
                Se a política mudou depois de um fechamento anterior, as linhas antigas ficam como
                estão — competência fechada não muda porque alguém clicou de novo.
              </AlertDescription>
            </Alert>

            {fechar.isError ? (
              <Alert variant="danger">
                <AlertDescription>{errorMessage(fechar.error)}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={aoFechar}
                disabled={fechar.isPending}
              >
                Cancelar
              </Button>

              {bloqueado ? (
                podeDefinirPolitica ? (
                  <Button type="button" onClick={aoAbrirPolitica}>
                    <SlidersHorizontal aria-hidden="true" />
                    Definir a política
                  </Button>
                ) : null
              ) : (
                <Button type="button" onClick={confirmar} disabled={fechar.isPending}>
                  <CalendarCheck aria-hidden="true" />
                  {fechar.isPending ? 'Fechando…' : `Fechar ${rotulo}`}
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <dl className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-surface p-3">
                <dt className="text-xs text-fg-muted">Veículos processados</dt>
                <dd className="numeric-display mt-1 text-xl">{resultado.veiculosProcessados}</dd>
              </div>
              <div className="rounded-lg border border-border bg-surface p-3">
                <dt className="text-xs text-fg-muted">Linhas criadas</dt>
                <dd className="numeric-display mt-1 text-xl">{resultado.linhasCriadas}</dd>
              </div>
              <div className="rounded-lg border border-border bg-surface p-3">
                <dt className="text-xs text-fg-muted">Já existiam</dt>
                <dd className="numeric-display mt-1 text-xl">{resultado.linhasJaExistentes}</dd>
              </div>
            </dl>

            {resultado.linhasCriadas === 0 ? (
              <Alert variant="info">
                <AlertDescription>
                  Nada foi criado porque esta competência já estava fechada. É o resultado esperado
                  de rodar duas vezes — nenhum custo foi duplicado.
                </AlertDescription>
              </Alert>
            ) : null}

            <div>
              <p className="text-sm font-medium text-foreground">
                O que o fechamento calculou para o mês
              </p>
              <ul className="mt-2 space-y-1 rounded-lg border border-border bg-surface p-3 text-sm">
                {resultado.totalPorTipo.map((linha) => (
                  <li key={linha.tipo} className="flex items-center justify-between gap-3">
                    <span className="text-fg-secondary">{ROTULO_DO_TIPO_DE_CUSTO[linha.tipo]}</span>
                    <span className="money-cell text-foreground">
                      {formatarMoedaDoWire(linha.valor)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-fg-muted">
                É o valor calculado agora. Linhas gravadas em um fechamento anterior foram
                preservadas e podem divergir deste total se a política mudou no meio do caminho.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" onClick={aoFechar}>
                Entendi
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Confirmação de estorno.
 *
 * Custo estornado SAI mesmo — ao contrário de conta e usuário. Um lançamento
 * errado não é fato do passado a preservar, é número errado somando no
 * relatório; o instantâneo completo fica no `audit_log`. As parcelas de rateio
 * vão junto, em cascata, e é isso que a confirmação precisa dizer em voz alta:
 * estornar um IPVA rateado apaga doze linhas, não uma.
 */
function DialogoDeEstorno({
  custo,
  aoFechar,
}: {
  readonly custo: Custo | null
  readonly aoFechar: () => void
}): ReactElement {
  const estornar = useEstornarCusto()

  // O erro do estorno anterior não pode reaparecer sobre outro lançamento: a
  // pessoa leria "não foi possível estornar" de um custo que nem abriu.
  useEffect(() => {
    if (custo !== null) {
      estornar.reset()
    }
    // `estornar` é recriado a cada render pelo TanStack Query; a intenção é
    // limpar UMA vez, quando um lançamento novo entra em confirmação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custo])

  function confirmar(): void {
    if (custo === null) {
      return
    }

    estornar.mutate(custo.id, {
      onSuccess: () => {
        toast.success('Lançamento estornado', {
          description:
            custo.parcelas > 0
              ? `As ${String(custo.parcelas)} parcelas de rateio saíram junto.`
              : 'A linha saiu do relatório.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível estornar', { description: errorMessage(causa) })
      },
    })
  }

  return (
    <Dialog
      open={custo !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Estornar este lançamento?</DialogTitle>
          <DialogDescription>
            A linha sai do relatório de verdade. O registro completo continua no log de auditoria.
          </DialogDescription>
        </DialogHeader>

        {custo === null ? null : (
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-surface p-4">
              <p className="truncate font-medium text-foreground">
                {identificacaoDoVeiculo(custo.veiculo)}
              </p>
              <p className="mt-1 truncate text-xs text-fg-secondary">
                {ROTULO_DO_TIPO_DE_CUSTO[custo.tipo]} · {formatarData(custo.data)}
              </p>
              <p className="money-cell mt-2 text-lg text-foreground">
                {formatarMoedaDoWire(custo.valor)}
              </p>
            </div>

            {custo.parcelas > 0 ? (
              <Alert variant="warning">
                <AlertTitle>
                  Este lançamento gerou {custo.parcelas} parcelas de competência
                </AlertTitle>
                <AlertDescription>
                  Todas saem junto, em cascata. O mês de caixa e os meses de competência voltam ao
                  que eram antes deste lançamento.
                </AlertDescription>
              </Alert>
            ) : null}

            {estornar.isError ? (
              <Alert variant="danger">
                <AlertDescription>{errorMessage(estornar.error)}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={aoFechar}
                disabled={estornar.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmar}
                disabled={estornar.isPending}
              >
                <Undo2 aria-hidden="true" />
                {estornar.isPending ? 'Estornando…' : 'Estornar'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function CustosPage(): ReactElement {
  const podeLancar = usePermissao('vehicle_cost.create')
  const podeCorrigir = usePermissao('vehicle_cost.update')
  const podeEstornar = usePermissao('vehicle_cost.delete')
  const podeVerPolitica = usePermissao('cost_policy.read')
  const podeEditarPolitica = usePermissao('cost_policy.update')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [tipo, setTipo] = useState<TipoDeCustoDeVeiculo | ''>('')
  const [origem, setOrigem] = useState<OrigemDoCusto | ''>('')
  const [mesDe, setMesDe] = useState('')
  const [mesAte, setMesAte] = useState('')

  // A competência do painel de resumo é independente da faixa dos filtros: uma
  // responde "como foi março?", a outra "onde está aquele lançamento?".
  const [mesDoResumo, setMesDoResumo] = useState(() => competenciaAtual().slice(0, 7))

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Custo | undefined>(undefined)
  const [custoParaEstornar, setCustoParaEstornar] = useState<Custo | null>(null)
  const [politicaAberta, setPoliticaAberta] = useState(false)
  const [fechamentoAberto, setFechamentoAberto] = useState(false)

  // O botão "Lançamentos" do resumo muda os filtros de uma seção que, em 375px,
  // está duas telas abaixo — sem levar a página até lá, o clique não parece ter
  // feito nada.
  const secaoDeLancamentos = useRef<HTMLElement>(null)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      vehicleId,
      tipo,
      origem,
      // O `<input type="month">` devolve `AAAA-MM`; a API exige o primeiro dia
      // do mês, e ela recusa qualquer outro dia de propósito.
      competenciaDe: mesDe === '' ? '' : `${mesDe}-01`,
      competenciaAte: mesAte === '' ? '' : `${mesAte}-01`,
    }),
    [pagina, busca, vehicleId, tipo, origem, mesDe, mesAte],
  )

  const consulta = useCustos(filtros)

  /**
   * O `<input type="month">` devolve string vazia enquanto a pessoa edita os
   * segmentos e quando ela limpa o campo. Sem esta volta ao mês corrente, a
   * competência viraria `-01`: requisição inválida e cabeçalho "Custo de —".
   */
  const mesEfetivoDoResumo = mesDoResumo === '' ? competenciaAtual().slice(0, 7) : mesDoResumo
  const competenciaDoResumo = `${mesEfetivoDoResumo}-01`

  const resumo = useResumoDeCustos(competenciaDoResumo)
  const veiculos = useVeiculos(TODOS_OS_VEICULOS)

  /**
   * A política é lida sem condicional: todo papel que chega a esta tela
   * (`vehicle_cost.read`) recebe `cost_policy.read` no mesmo pacote do seed. Se
   * um papel sob medida não tiver, esta consulta falha com 403 e a tela apenas
   * deixa de mostrar o aviso — nenhum estado quebrado, e nenhuma outra
   * requisição depende dela.
   */
  const politica = usePoliticaDeCusto()
  const politicaDefinida = politica.data === undefined ? null : politica.data.definida

  const temFiltro =
    busca.length > 0 ||
    vehicleId !== '' ||
    tipo !== '' ||
    origem !== '' ||
    mesDe !== '' ||
    mesAte !== ''

  function limparFiltros(): void {
    setBuscaDigitada('')
    setVehicleId('')
    setTipo('')
    setOrigem('')
    setMesDe('')
    setMesAte('')
    setPagina(1)
  }

  function abrirLancamento(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirCorrecao(custo: Custo): void {
    setEmEdicao(custo)
    setDialogoAberto(true)
  }

  /** Leva a faixa de filtros para a competência do resumo, no veículo clicado. */
  function verLancamentosDoVeiculo(linha: LinhaDoResumoDeCusto): void {
    setVehicleId(linha.vehicleId)
    setMesDe(mesEfetivoDoResumo)
    setMesAte(mesEfetivoDoResumo)
    setPagina(1)
    secaoDeLancamentos.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  /**
   * Referência estável: `?? []` cria um array novo a cada render, e ele é
   * dependência de três `useMemo` — sem memoizar, os três recalculariam sempre.
   */
  const itensDoResumo = useMemo(() => resumo.data?.itens ?? [], [resumo.data])

  const caixaPorTipo = useMemo(() => somarPorTipo(itensDoResumo, 'caixa'), [itensDoResumo])
  const competenciaPorTipo = useMemo(
    () => somarPorTipo(itensDoResumo, 'competencia'),
    [itensDoResumo],
  )

  /** A escala das barras: o maior valor da tabela, de qualquer uma das origens. */
  const maiorValor = useMemo(
    () =>
      itensDoResumo.reduce<Money>((maximo, linha) => {
        const caixa = centavos(linha.caixa.total)
        const competencia = centavos(linha.competencia.total)
        const maiorDaLinha = caixa > competencia ? caixa : competencia

        return maiorDaLinha > maximo ? maiorDaLinha : maximo
      }, fromCents(0)),
    [itensDoResumo],
  )

  const colunasDoResumo = useMemo<ColumnDef<LinhaDoResumoDeCusto>[]>(
    () => [
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha: LinhaDoResumoDeCusto) => linha.placa,
        cell: ({ row }) => {
          const linha: LinhaDoResumoDeCusto = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">
                {identificacaoDoVeiculo(linha)}
              </p>
              <p className="truncate text-xs text-fg-secondary">{linha.marca}</p>
            </div>
          )
        },
      },
      {
        id: 'caixa',
        header: 'Caixa (saiu no mês)',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: LinhaDoResumoDeCusto) => chaveDeOrdenacaoDeValor(linha.caixa.total),
        cell: ({ row }) => {
          const linha: LinhaDoResumoDeCusto = row.original

          return (
            <div className="min-w-28">
              <span className="money-cell block text-foreground">
                {formatarMoedaDoWire(linha.caixa.total)}
              </span>
              <BarraDeProporcao valor={linha.caixa.total} maior={maiorValor} cor="bg-chart-4" />
            </div>
          )
        },
      },
      {
        id: 'competencia',
        header: 'Competência (consumiu no mês)',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: LinhaDoResumoDeCusto) =>
          chaveDeOrdenacaoDeValor(linha.competencia.total),
        cell: ({ row }) => {
          const linha: LinhaDoResumoDeCusto = row.original

          return (
            <div className="min-w-28">
              <span className="money-cell block text-foreground">
                {formatarMoedaDoWire(linha.competencia.total)}
              </span>
              <BarraDeProporcao
                valor={linha.competencia.total}
                maior={maiorValor}
                cor="bg-chart-2"
              />
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              verLancamentosDoVeiculo(row.original)
            }}
          >
            <Receipt aria-hidden="true" />
            Lançamentos
          </Button>
        ),
      },
    ],
    // `verLancamentosDoVeiculo` muda de identidade a cada render, mas o que ele
    // CAPTURA são estas duas dependências — a competência vai junto porque o
    // clique leva a faixa de filtros para o mês que está no resumo, e uma
    // closure velha mandaria a pessoa para o mês anterior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maiorValor, mesEfetivoDoResumo],
  )

  const colunas = useMemo<ColumnDef<Custo>[]>(
    () => [
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha: Custo) => linha.veiculo.placa,
        cell: ({ row }) => {
          const custo: Custo = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">
                {identificacaoDoVeiculo(custo.veiculo)}
              </p>
              <p className="truncate text-xs text-fg-secondary">{custo.veiculo.marca}</p>
            </div>
          )
        },
      },
      {
        id: 'tipo',
        header: 'Tipo',
        accessorFn: (linha: Custo) => ROTULO_DO_TIPO_DE_CUSTO[linha.tipo],
        cell: ({ row }) => {
          const custo: Custo = row.original
          const detalhe = [custo.fornecedor, custo.notaFiscal]
            .filter((valor): valor is string => valor !== null && valor.length > 0)
            .join(' · ')

          return (
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground">
                {ROTULO_DO_TIPO_DE_CUSTO[custo.tipo]}
              </p>
              {detalhe.length === 0 ? null : (
                <p className="truncate text-xs text-fg-secondary">{detalhe}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'origem',
        header: 'Origem',
        enableSorting: false,
        cell: ({ row }) => {
          const custo: Custo = row.original

          return (
            <div className="flex flex-col items-start gap-1">
              <BadgeDeOrigemDeCusto origem={custo.origem} />
              {custo.parcelas > 0 ? (
                <Badge variant="outline" className="tabular-money">
                  Rateado em {custo.parcelas} meses
                </Badge>
              ) : custo.custoDeOrigemId === null ? null : (
                <Badge variant="outline">Parcela de rateio</Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'competencia',
        header: 'Competência',
        accessorFn: (linha: Custo) => linha.competencia,
        cell: ({ row }) => {
          const custo: Custo = row.original

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm text-foreground">
                {rotuloDaCompetencia(custo.competencia)}
              </p>
              <p className="whitespace-nowrap text-xs tabular-money text-fg-secondary">
                gasto em {formatarData(custo.data)}
              </p>
            </div>
          )
        },
      },
      {
        id: 'valor',
        header: 'Valor',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Custo) => chaveDeOrdenacaoDeValor(linha.valor),
        cell: ({ row }) => (
          <span className="money-cell block text-foreground">
            {formatarMoedaDoWire(row.original.valor)}
          </span>
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const custo: Custo = row.original

          // Linha de competência é resultado de cálculo reproduzível: a API
          // recusa PATCH e DELETE nela, e a tela nem oferece o caminho. Só
          // sobra o comprovante, quando existe — e ele nunca existe aqui.
          const manual = custo.origem === 'LANCAMENTO_MANUAL'
          const temAnexo = custo.anexoUrl !== null && custo.anexoUrl.length > 0
          const podeAlgumaCoisa = temAnexo || (manual && (podeCorrigir || podeEstornar))

          if (!podeAlgumaCoisa) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações do custo de ${ROTULO_DO_TIPO_DE_CUSTO[custo.tipo]} de ${identificacaoDoVeiculo(custo.veiculo)}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                {temAnexo ? (
                  <DropdownMenuItem asChild>
                    <a href={custo.anexoUrl ?? ''} target="_blank" rel="noreferrer noopener">
                      <ExternalLink aria-hidden="true" />
                      Abrir comprovante
                    </a>
                  </DropdownMenuItem>
                ) : null}

                {manual && podeCorrigir ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirCorrecao(custo)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Corrigir dados
                  </DropdownMenuItem>
                ) : null}

                {manual && podeEstornar ? (
                  <>
                    {temAnexo || podeCorrigir ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      onSelect={() => {
                        setCustoParaEstornar(custo)
                      }}
                    >
                      <Undo2 aria-hidden="true" />
                      Estornar lançamento
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões.
    [podeCorrigir, podeEstornar],
  )

  const dados = consulta.data
  const listaDeVeiculos = veiculos.data?.itens ?? []

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Custos da frota</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            Caixa é o que saiu do banco no mês. Competência é o que o mês consumiu. O mesmo
            pagamento aparece nos dois — por isso eles nunca são somados.
          </p>
        </div>

        {podeLancar ? (
          <Button onClick={abrirLancamento}>
            <Plus aria-hidden="true" />
            Lançar custo
          </Button>
        ) : null}
      </div>

      {/* ------------------------------------------------------------------
          Competência: as duas origens lado a lado
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-da-competencia" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h2 id="titulo-da-competencia" className="text-base font-semibold tracking-tight">
              Custo de {rotuloDaCompetencia(competenciaDoResumo)}
            </h2>
            <p className="text-sm text-fg-secondary">
              Os dois recortes do mesmo dinheiro, veículo a veículo.
            </p>
          </div>

          {/* Em 375px o grupo ocupa a linha inteira: dividir ~340px entre o
              campo de mês e dois botões deixaria o seletor ilegível. */}
          <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
            <div className="min-w-36 flex-1 space-y-1.5 sm:flex-none">
              <Label htmlFor="competencia-do-resumo">Competência</Label>
              <Input
                id="competencia-do-resumo"
                type="month"
                value={mesDoResumo}
                onChange={(evento) => {
                  setMesDoResumo(evento.target.value)
                }}
                className="tabular-money"
              />
            </div>

            {podeVerPolitica ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setPoliticaAberta(true)
                }}
              >
                <SlidersHorizontal aria-hidden="true" />
                Política de custo
              </Button>
            ) : null}

            {podeLancar ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setFechamentoAberto(true)
                }}
              >
                <CalendarCheck aria-hidden="true" />
                Fechar competência
              </Button>
            ) : null}
          </div>
        </div>

        {podeVerPolitica && politicaDefinida === false ? (
          <Alert variant="warning">
            <AlertTitle>A política de custo indireto ainda não foi definida</AlertTitle>
            <AlertDescription>
              Depreciação, custo de capital e overhead não são calculados enquanto ninguém salvar
              vida útil, residual, taxa de capital e overhead mensal — e sem eles o painel mostra
              lucro que não existe.
            </AlertDescription>
          </Alert>
        ) : null}

        {podeVerPolitica && politicaDefinida === true && politica.data !== undefined ? (
          <p className="text-xs tabular-money text-fg-muted">
            Política atual: vida útil de {politica.data.vidaUtilEmMeses} meses, residual de{' '}
            {formatarPontosBaseComoPercentual(politica.data.residualEmPontosBase)}%, custo de
            capital de{' '}
            {formatarPontosBaseComoPercentual(politica.data.custoDeCapitalAnualEmPontosBase)}% ao
            ano e overhead de {formatarMoedaDoWire(politica.data.overheadMensal)} por mês.
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <CartaoDeTotal
            titulo="Caixa"
            explicacao="O que saiu do banco nesta competência. Não some com a coluna ao lado."
            total={resumo.data?.totais.caixa}
            porTipo={caixaPorTipo}
            icone={Banknote}
            carregando={resumo.isPending}
            comErro={resumo.isError}
          />
          <CartaoDeTotal
            titulo="Competência"
            explicacao="O que a frota consumiu nesta competência. Não some com a coluna ao lado."
            total={resumo.data?.totais.competencia}
            porTipo={competenciaPorTipo}
            icone={Calculator}
            carregando={resumo.isPending}
            comErro={resumo.isError}
            destaque
          />
        </div>

        <TabelaDeDados
          colunas={colunasDoResumo}
          dados={itensDoResumo}
          carregando={resumo.isPending}
          erro={resumo.isError ? resumo.error : undefined}
          aoTentarNovamente={() => {
            void resumo.refetch()
          }}
          legenda={`Custo por veículo na competência de ${rotuloDaCompetencia(competenciaDoResumo)}`}
          ordenacaoInicial={[{ id: 'competencia', desc: true }]}
          estadoVazio={
            <EmptyState
              icone={Calculator}
              titulo="Nenhum custo nesta competência"
              descricao="Sem lançamentos e sem fechamento, o mês fica zerado. Depreciação, custo de capital e overhead só existem depois que o fechamento roda."
              acao={
                podeLancar ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setFechamentoAberto(true)
                    }}
                  >
                    <CalendarCheck aria-hidden="true" />
                    Fechar {rotuloDaCompetencia(competenciaDoResumo)}
                  </Button>
                ) : undefined
              }
            />
          }
        />
      </section>

      <Separator />

      {/* ------------------------------------------------------------------
          Lançamentos
          ------------------------------------------------------------------ */}
      <section
        ref={secaoDeLancamentos}
        aria-labelledby="titulo-dos-lancamentos"
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id="titulo-dos-lancamentos" className="text-base font-semibold tracking-tight">
            Lançamentos
          </h2>
          <p className="text-sm text-fg-secondary">
            Toda linha traz a origem. Filtre por uma delas antes de exportar qualquer total.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="filtro-busca-custos">Buscar</Label>
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                  text-fg-muted"
              />
              <Input
                id="filtro-busca-custos"
                type="search"
                value={buscaDigitada}
                onChange={(evento) => {
                  setPagina(1)
                  setBuscaDigitada(evento.target.value)
                }}
                placeholder="Fornecedor, nota fiscal ou placa"
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-veiculo-custos">Veículo</Label>
            <Select
              id="filtro-veiculo-custos"
              value={vehicleId}
              onChange={(evento) => {
                setPagina(1)
                setVehicleId(evento.target.value)
              }}
              disabled={veiculos.isPending || veiculos.isError}
            >
              <option value="">{veiculos.isPending ? 'Carregando…' : 'Todos'}</option>
              {listaDeVeiculos.map((veiculo) => (
                <option key={veiculo.id} value={veiculo.id}>
                  {identificacaoDoVeiculo(veiculo)}
                </option>
              ))}
            </Select>

            {/* Um `<select>` desabilitado e mudo pareceria uma tela travada; a
                lista de custos continua funcionando sem este filtro. */}
            {veiculos.isError ? (
              <p className="text-xs text-danger-fg">
                Não foi possível carregar a frota — filtre por placa na busca.
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-origem-custos">Origem</Label>
            <Select
              id="filtro-origem-custos"
              value={origem}
              onChange={(evento) => {
                setPagina(1)
                const valor = evento.target.value
                setOrigem(ORIGENS_DE_CUSTO.find((item) => item === valor) ?? '')
              }}
            >
              <option value="">Caixa e competência</option>
              {ORIGENS_DE_CUSTO.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DA_ORIGEM[item]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-tipo-custos">Tipo</Label>
            <Select
              id="filtro-tipo-custos"
              value={tipo}
              onChange={(evento) => {
                setPagina(1)
                const valor = evento.target.value
                setTipo(TIPOS_DE_CUSTO_DE_VEICULO.find((item) => item === valor) ?? '')
              }}
            >
              <option value="">Todos</option>
              {TIPOS_DE_CUSTO_DE_VEICULO.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DO_TIPO_DE_CUSTO[item]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-competencia-de">Competência de</Label>
            <Input
              id="filtro-competencia-de"
              type="month"
              value={mesDe}
              onChange={(evento) => {
                setPagina(1)
                setMesDe(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-competencia-ate">Competência até</Label>
            <Input
              id="filtro-competencia-ate"
              type="month"
              value={mesAte}
              onChange={(evento) => {
                setPagina(1)
                setMesAte(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>
        </div>

        <TabelaDeDados
          colunas={colunas}
          dados={dados?.itens ?? []}
          carregando={consulta.isPending}
          erro={consulta.isError ? consulta.error : undefined}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
          legenda="Lançamentos de custo da frota"
          estadoVazio={
            <EmptyState
              icone={Receipt}
              titulo={temFiltro ? 'Nenhum custo com esses filtros' : 'Nenhum custo lançado'}
              descricao={
                temFiltro
                  ? 'Ajuste a busca, o veículo ou a faixa de competência para ver outros resultados.'
                  : 'Lance o que sai do caixa — combustível, lavagem, IPVA, seguro. Depreciação e overhead vêm do fechamento mensal.'
              }
              acao={
                temFiltro ? (
                  <Button variant="secondary" onClick={limparFiltros}>
                    Limpar filtros
                  </Button>
                ) : podeLancar ? (
                  <Button onClick={abrirLancamento}>
                    <Plus aria-hidden="true" />
                    Lançar custo
                  </Button>
                ) : undefined
              }
            />
          }
        />

        <Paginacao
          pagina={dados?.pagina ?? pagina}
          tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
          total={dados?.total ?? 0}
          aoMudarDePagina={setPagina}
          desabilitado={consulta.isFetching}
        />
      </section>

      <DialogoDeCusto
        aberto={dialogoAberto}
        custo={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />

      <DialogoDeEstorno
        custo={custoParaEstornar}
        aoFechar={() => {
          setCustoParaEstornar(null)
        }}
      />

      {podeVerPolitica ? (
        <DialogoDePoliticaDeCusto
          aberto={politicaAberta}
          podeEditar={podeEditarPolitica}
          aoFechar={() => {
            setPoliticaAberta(false)
          }}
        />
      ) : null}

      {podeLancar ? (
        <DialogoDeFechamento
          aberto={fechamentoAberto}
          competencia={competenciaDoResumo}
          politicaDefinida={politicaDefinida}
          podeDefinirPolitica={podeEditarPolitica}
          aoAbrirPolitica={() => {
            setFechamentoAberto(false)
            setPoliticaAberta(true)
          }}
          aoFechar={() => {
            setFechamentoAberto(false)
          }}
        />
      ) : null}
    </div>
  )
}
